import { useEffect, useMemo } from 'react'
import * as Sentry from '@sentry/browser'

import config from '@sketch/env-config'

// Hooks
import useUserSignedIn from 'modules/user/useUserSignedIn'
import { domains } from '@sketch/constants'

// Types
type EventType = 'event'
type EventValue = 'page_view'
type TrackEvent = [EventType, EventValue, EventProps]

interface EventProps {
  page_path: string
}

// Helpers
const GA_PROPERTY = config.google_analytics

// Creates a new iframe to inject GA script otherwise returns the already created iframe
const getOrCreateIframe = () => {
  const gaIframe = document.getElementById('ga-iframe')
  if (gaIframe) {
    return gaIframe as HTMLIFrameElement
  }

  // Create an iframe to include the GA script
  const iframe = document.createElement('iframe')

  iframe.setAttribute('id', 'ga-iframe')
  iframe.setAttribute('hidden', 'true')

  // Add the GA script to the iframe
  const html = `
  <head>
    <script src="https://www.googletagmanager.com/gtag/js?id=${GA_PROPERTY}"></script>
  </head>
  `

  document.body.appendChild(iframe)
  iframe.contentWindow?.document.write(html)

  // Close the document to prevent parent document to keep loading
  iframe.contentWindow?.document.close()

  // Init GA
  iframe.contentWindow!.dataLayer = iframe.contentWindow?.dataLayer || []

  function gtag(...args: any[]) {
    // eslint-disable-next-line prefer-rest-params
    iframe.contentWindow?.dataLayer?.push(arguments)
  }

  gtag('js', new Date())
  gtag('config', GA_PROPERTY, {
    send_page_view: false,
    anonymize_ip: true,
  })
  gtag('set', { dimension1: 'Anonymous' })

  return iframe
}

// Create a function that makes sure Sentry is only
// triggered once if GA property doesn't exist and
// trackEvent is called multiple times
const createSentryUniqueWarning = () => {
  let messageSent = false

  return () => {
    if (messageSent) return

    Sentry.withScope(() => {
      Sentry.captureMessage('Google Analytics ID was not found')
    })

    messageSent = true
  }
}

const uniqueSentryWarning = createSentryUniqueWarning()

const resetCookie = (cookie: string) => {
  const cookieName = cookie.split('=')[0].trim()

  // Remove all cookies for all listed domains
  domains.forEach(domain => {
    document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${domain}`
  })
}

/**
 * useGoogleAnalytics
 *
 * We are very strict of what we track and how long the
 * GA cookie remains. We only track anonymous pages (Sign in and Sign up).
 *
 * For that reason, this hook creates and iframe with GA script
 * and tracks a pageview. As soon as the unmount ocurrs we remove the iframe and cookies
 *
 * This limits GA presence on purpose, for now.
 * */
export const useGoogleAnalytics = () => {
  const isSignedIn = useUserSignedIn()

  const removeGACookies = () => {
    // Remove iframe
    const gaIframe = document.getElementById('ga-iframe')

    if (gaIframe) {
      document.body.removeChild(gaIframe)
    }

    // Remove all cookies)
    document.cookie
      .split(';')
      .filter(cookie => cookie.trim().startsWith('_g'))
      .forEach(resetCookie)
  }

  useEffect(() => {
    // Because we ONLY want GA in anonymous pages (Sign in, Sign up)
    // we remove the iframe and all the GA cookies on unmount
    return () => {
      removeGACookies()
    }
  }, [])

  const memoizedTrackGAEvent = useMemo(
    () => ({
      trackGAEvent: function trackGAEvent(..._args: TrackEvent) {
        // We really don't want Google Analytics anywhere near non-anonymous pages.
        if (isSignedIn) {
          removeGACookies()
          return
        }

        // The iframe was causing Cypress to timeout in the visit command, even if the iframe content
        // was an empty string. Since we don't want this to run on cypress we are returning null here.
        if (window?.Cypress) {
          return
        }

        if (!GA_PROPERTY) {
          uniqueSentryWarning()

          return
        }

        const iframe = getOrCreateIframe()

        // Push event to the dataLayer
        // We are using the "arguments" to forward all gtag() arguments to the dataLayer
        // eslint-disable-next-line prefer-rest-params
        iframe.contentWindow?.dataLayer?.push(arguments)
      },
    }),
    [isSignedIn]
  )

  return { ...memoizedTrackGAEvent, removeGACookies }
}
